/**
 * Australian phone number normalization for the client portal. Staff shouldn't have to know
 * or type E.164 -- they enter numbers the way people actually write them ("0412 234 345",
 * "8627 3765", "03-8627 3765") and this converts to the `+61...` form the database and the
 * voice service actually need (call_routes.destination_e164, customer_phones.e164, etc.).
 */

export const AU_STATES = [
  "New South Wales",
  "Victoria",
  "Queensland",
  "Western Australia",
  "South Australia",
  "Tasmania",
  "Australian Capital Territory",
  "Northern Territory",
] as const;

/** Landline area code by state -- the only ambiguous case is an 8-digit number with no area
 *  code at all, which this table resolves using the business's own state. */
const STATE_AREA_CODE: Record<string, string> = {
  "New South Wales": "02",
  "Australian Capital Territory": "02",
  Victoria: "03",
  Tasmania: "03",
  Queensland: "07",
  "Western Australia": "08",
  "South Australia": "08",
  "Northern Territory": "08",
};

export type PhoneNormalizeResult = { ok: true; e164: string } | { ok: false; error: string };

/** Converts a staff-entered AU phone number into E.164 (`+61...`). `state` is only consulted
 *  for an 8-digit landline number with no area code -- every other shape is unambiguous on its
 *  own. Returns a caller-facing error (not a thrown exception) for anything that can't be
 *  resolved, e.g. an 8-digit number when no business state is set yet. */
export function normalizeAuPhone(raw: string, state: string | null): PhoneNormalizeResult {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, error: "Enter a phone number." };

  if (trimmed.startsWith("+")) {
    const digits = trimmed.slice(1).replace(/\D/g, "");
    if (digits.startsWith("61") && digits.length === 11) return { ok: true, e164: `+${digits}` };
    return { ok: false, error: "That doesn't look like a valid +61 number." };
  }

  const digits = trimmed.replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("61")) return { ok: true, e164: `+${digits}` };
  if (digits.length === 10 && /^1[38]00/.test(digits)) {
    // 1300 / 1800 numbers have no leading 0 nationally: +611300123456.
    return { ok: true, e164: `+61${digits}` };
  }
  if (digits.length === 10 && digits.startsWith("0")) {
    return { ok: true, e164: `+61${digits.slice(1)}` };
  }
  if (digits.length === 9 && digits.startsWith("4")) {
    // Mobile typed without the leading 0 (e.g. "412 234 345").
    return { ok: true, e164: `+61${digits}` };
  }
  if (digits.length === 8) {
    if (!state) {
      return { ok: false, error: "That number has no area code -- set the business's state first, or include the area code (e.g. 03 8627 3765)." };
    }
    const areaCode = STATE_AREA_CODE[state];
    if (!areaCode) return { ok: false, error: `No area code known for "${state}".` };
    return { ok: true, e164: `+61${areaCode.slice(1)}${digits}` };
  }

  return { ok: false, error: "Enter a valid Australian number, e.g. 0412 345 678 or 03 9235 2345." };
}

/** How every phone number is shown in the UI, the standard Australian way:
 *  mobile "0423 345 678", landline "03 9235 2345", 13/1800 numbers "1300 123 456".
 *  Accepts E.164 ("+61423345678") or an already-national number, with or without spaces.
 *  Returns the value unchanged for anything else (e.g. a withheld caller ID) rather than guessing. */
export function formatAuPhoneForDisplay(value: string): string {
  const compact = value.replace(/[\s()-]/g, "");
  let national: string | null = null;
  const e164 = compact.match(/^\+61(\d{9,10})$/);
  if (e164) national = e164[1]!.length === 9 ? `0${e164[1]}` : e164[1]!;
  else if (/^\d{10}$/.test(compact)) national = compact;
  if (!national) return value;
  if (/^1[38]00\d{6}$/.test(national)) return `${national.slice(0, 4)} ${national.slice(4, 7)} ${national.slice(7)}`;
  if (/^04\d{8}$/.test(national)) return `${national.slice(0, 4)} ${national.slice(4, 7)} ${national.slice(7)}`;
  if (/^0[2378]\d{8}$/.test(national)) return `${national.slice(0, 2)} ${national.slice(2, 6)} ${national.slice(6)}`;
  return value;
}
